import { ReactNode, useEffect, useMemo, useState } from "react";
import { Box, InputBase, Tooltip } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { currencyFormatter } from "../../../../utils/currencyFormatter";
import { useBudget } from "../../../../contexts/BudgetContext/projectbudgets.context";
import { NoteAdvance } from "../../../../types/budget/budget";

const BLUE = "#1361A4";
const VALUE_BLUE = "#0F4F89";

type Props = {
  /** Subtotal do Bloco A */
  notaFornecedores: number;
  /** Subtotal do Bloco B */
  notaDebito: number;
  /** Imposto (tribute) do bloco Total */
  imposto: number;
  /** Taxa MM (fee) do bloco Total */
  taxaMM: number;
  /** Subtotal do Bloco C */
  subtotalC: number;
  /** Subtotal do Bloco D */
  subtotalD: number;
};

/** Converte texto digitado (pt-BR) em número. Ex.: "10.000,00" -> 10000 */
function toNumber(value: string): number {
  if (!value) return 0;
  const normalized = value.replace(/\./g, "").replace(",", ".");
  const parsed = parseFloat(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
}

/** Formata o valor salvo (número) para exibição no input. Ex.: 10000 -> "10.000,00" */
function formatInitial(value?: number): string {
  if (!value) return "";
  return value.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Máscara pt-BR aplicada enquanto digita. Ex.: "100000" -> "1.000,00" */
function maskCurrency(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (!digits) return "";
  const value = parseInt(digits, 10) / 100;
  return value.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export default function NotaSummary({
  notaFornecedores,
  notaDebito,
  imposto,
  taxaMM,
  subtotalC,
  subtotalD,
}: Props) {
  const { activeBudget, versionInfo, activeBudgetDispatch } = useBudget();

  const notaFiscal = useMemo(
    () => imposto + taxaMM + subtotalC + subtotalD,
    [imposto, taxaMM, subtotalC, subtotalD],
  );

  const savedAdvance = useMemo(
    () =>
      activeBudget.noteAdvances?.find(
        (el) => el.version === versionInfo.version,
      ),
    [activeBudget.noteAdvances, versionInfo.version],
  );

  const [adiantamentoDebito, setAdiantamentoDebito] = useState(() =>
    formatInitial(savedAdvance?.advanceDebitNote),
  );
  const [adiantamentoFiscal, setAdiantamentoFiscal] = useState(() =>
    formatInitial(savedAdvance?.advanceTaxNote),
  );
  // Recarrega os campos com o valor salvo ao trocar de orçamento ou de versão.
  useEffect(() => {
    setAdiantamentoDebito(formatInitial(savedAdvance?.advanceDebitNote));
    setAdiantamentoFiscal(formatInitial(savedAdvance?.advanceTaxNote));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBudget._id, versionInfo.version]);

  const restanteDebito = notaDebito - toNumber(adiantamentoDebito);
  const restanteFiscal = notaFiscal - toNumber(adiantamentoFiscal);

  const handleChangeDebito = (value: string) => {
    setAdiantamentoDebito(maskCurrency(value));
  };
  const handleChangeFiscal = (value: string) => {
    setAdiantamentoFiscal(maskCurrency(value));
  };

  // Ao sair do campo, joga o valor pra dentro do activeBudget (via reducer),
  // garantindo que qualquer PUT (autosave, outros saves) já leve o noteAdvances.
  const commitAdvance = () => {
    const advance: NoteAdvance = {
      version: versionInfo.version,
      advanceDebitNote: toNumber(adiantamentoDebito),
      advanceTaxNote: toNumber(adiantamentoFiscal),
    };
    activeBudgetDispatch({
      type: "SET_NOTE_ADVANCE",
      payload: { noteAdvance: advance },
      versionInfo,
    });
  };

  return (
    <Box
      sx={{
        mt: 3,
        p: 3,
        backgroundColor: "#fff",
        borderRadius: "8px",
        boxShadow: "0px 4px 18px 0px #4B465C1A",
      }}
    >
      {/* Cabeçalho */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DescriptionOutlinedIcon sx={{ color: BLUE, fontSize: 22 }} />
          <span
            style={{
              color: BLUE,
              fontWeight: 700,
              fontSize: 16,
              letterSpacing: "0.5px",
            }}
          >
            RESUMO DE NOTA
          </span>
        </Box>

        <Box sx={{ display: "flex", width: "50%" }}>
          <ColumnHeader label="ADIANTAMENTO (R$)" />
          <ColumnHeader label="VALOR RESTANTE (R$)" />
        </Box>
      </Box>

      {/* Tabela */}
      <Box
        sx={{
          border: "1px solid #E7E7EC",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        {/* Nota de Fornecedores (Bloco A) — sem adiantamento */}
        <Row
          label="NOTA DE FORNECEDORES FATURADO DIRETO CONTRA O CLIENTE"
          tooltip="Subtotal do Bloco A (fornecedores faturados direto contra o cliente)."
          value={notaFornecedores}
          adiantamento={<Placeholder />}
          restante={<Placeholder />}
        />

        {/* Nota de Débito (Bloco B) */}
        <Row
          label="NOTA DE DÉBITO"
          tooltip="Subtotal do Bloco B."
          value={notaDebito}
          adiantamento={
            <AdiantamentoInput
              value={adiantamentoDebito}
              onChange={handleChangeDebito}
              onBlur={commitAdvance}
            />
          }
          restante={<RestanteValue value={restanteDebito} />}
        />

        {/* Nota Fiscal (Imposto + Taxa MM + Bloco C + Bloco D) */}
        <Row
          label="NOTA FISCAL"
          tooltip="Imposto + Taxa MM (do Total) + Subtotal do Bloco C + Subtotal do Bloco D."
          value={notaFiscal}
          breakdown={
            <>
              (Imposto {currencyFormatter.format(imposto)} + Taxa MM{" "}
              {currencyFormatter.format(taxaMM)} +<br />
              Subtotal Bloco C {currencyFormatter.format(subtotalC)} + Subtotal
              Bloco D {currencyFormatter.format(subtotalD)})
            </>
          }
          adiantamento={
            <AdiantamentoInput
              value={adiantamentoFiscal}
              onChange={handleChangeFiscal}
              onBlur={commitAdvance}
            />
          }
          restante={<RestanteValue value={restanteFiscal} />}
          isLast
        />

        {/* Rodapé com as fórmulas */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            backgroundColor: "#F0F7FF",
            px: 2,
            py: 2.5,
          }}
        >
          <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            <FormulaNote
              bold="VALOR RESTANTE NOTA DE DÉBITO"
              rest="= NOTA DE DÉBITO - ADIANTAMENTO NOTA DE DÉBITO"
            />
            <FormulaNote
              bold="VALOR RESTANTE NOTA FISCAL"
              rest="= NOTA FISCAL - ADIANTAMENTO NOTA FISCAL"
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function ColumnHeader({ label }: { label: string }) {
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        justifyContent: "center",
      }}
    >
      <span style={{ color: "#8A8A9A", fontSize: 12, letterSpacing: "0.3px" }}>
        {label}
      </span>
    </Box>
  );
}

type RowProps = {
  label: string;
  tooltip: string;
  value: number;
  breakdown?: ReactNode;
  adiantamento: ReactNode;
  restante: ReactNode;
  isLast?: boolean;
};

function Row({
  label,
  tooltip,
  value,
  breakdown,
  adiantamento,
  restante,
  isLast,
}: RowProps) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "stretch",
        borderBottom: isLast ? "none" : "1px dashed #E0E0E8",
      }}
    >
      {/* Label + valor */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          px: 2,
          py: 2,
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: "#2F2B3D" }}>
              {label}
            </span>
            <Tooltip title={tooltip}>
              <InfoOutlinedIcon sx={{ color: "#B0B0BC", fontSize: 16 }} />
            </Tooltip>
          </Box>
          {breakdown && (
            <span
              style={{
                display: "block",
                marginTop: 4,
                fontSize: 12,
                color: "#8A8A9A",
                lineHeight: 1.4,
              }}
            >
              {breakdown}
            </span>
          )}
        </Box>
        <span style={{ fontSize: 14, color: "#2F2B3D", whiteSpace: "nowrap" }}>
          {currencyFormatter.format(value)}
        </span>
      </Box>

      {/* Colunas Adiantamento / Valor Restante */}
      <Box
        sx={{
          display: "flex",
          width: "50%",
          flexShrink: 0,
          borderLeft: "1px solid #E7E7EC",
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 1,
          }}
        >
          {adiantamento}
        </Box>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 1,
          }}
        >
          {restante}
        </Box>
      </Box>
    </Box>
  );
}

function Placeholder() {
  return <span style={{ color: "#B0B0BC", fontSize: 14 }}>-</span>;
}

function RestanteValue({ value }: { value: number }) {
  return (
    <span style={{ color: VALUE_BLUE, fontWeight: 700, fontSize: 14 }}>
      {currencyFormatter.format(value)}
    </span>
  );
}

function AdiantamentoInput({
  value,
  onChange,
  onBlur,
}: {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
}) {
  return (
    <InputBase
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      placeholder="0,00"
      sx={{
        width: 140,
        border: "1px solid #D8D6E3",
        borderRadius: "6px",
        px: 1.5,
        py: 0.5,
        fontSize: 14,
        "& input": { textAlign: "center", padding: 0 },
      }}
    />
  );
}

function FormulaNote({ bold, rest }: { bold: string; rest: string }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <InfoOutlinedIcon sx={{ color: "#B0B0BC", fontSize: 16 }} />
      <span style={{ fontSize: 12 }}>
        <span style={{ fontWeight: 700, color: "#5A5566" }}>{bold} </span>
        <span style={{ color: "#9A96A6" }}>{rest}</span>
      </span>
    </Box>
  );
}
